import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, useMotionValue } from "framer-motion";
import { RefreshCw, ArrowLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { MoodCard } from "../components/MoodCard";
import PackSwitcher from "../components/PackSwitcher";
import ThemeToggle from "../components/ThemeToggle";
import { Toaster } from "../components/ui/sonner";
import { getMoodCard, regenerateMoodCard, switchPack } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/track";
import TourHint from "../components/TourHint";
import { tick } from "../lib/haptic";

export default function Results() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [mood, setMood] = useState(location.state?.mood || null);
    const [loading, setLoading] = useState(!mood);
    const [regenerating, setRegenerating] = useState(false);
    const [packLoading, setPackLoading] = useState(false);
    // Premium-pack gate: only users on premium/creator tier can apply
    // cosmic / romantic / dark packs. Plus is treated as below-the-line for
    // material packs — those are the deliberate upgrade hook.
    const hasPremiumPacks = user?.tier === "premium" || user?.tier === "creator";

    useEffect(() => {
        if (mood) { track("results_view", { pack: mood.pack, id: mood.id }); return; }
        (async () => {
            try {
                const data = await getMoodCard(id);
                setMood(data);
            } catch (e) {
                console.error(e);
                navigate("/", { replace: true });
            } finally {
                setLoading(false);
            }
        })();
    }, [id, mood, navigate]);

    const handleRegenerate = async () => {
        setRegenerating(true);
        try {
            const data = await regenerateMoodCard(id);
            setMood(data);
            setActive(0);
        } catch (e) { console.error(e); }
        finally { setTimeout(() => setRegenerating(false), 300); }
    };

    const handlePack = async (pack) => {
        if (!mood || pack === mood.pack) return;
        track("pack_switch", { from: mood.pack, to: pack });
        setPackLoading(true);
        try {
            const data = await switchPack(id, pack);
            setMood(data);
            setActive(0);
        } catch (e) {
            // 402 = premium-required (server-side double-check). Show upgrade nudge.
            if (e?.response?.status === 402) {
                track("premium_pack_locked_server", { pack });
                toast.error(`${pack.charAt(0).toUpperCase() + pack.slice(1)} is a Premium material. Upgrade to unlock.`);
            } else {
                console.error(e);
            }
        }
        finally { setPackLoading(false); }
    };

    const handlePremiumLocked = (pack) => {
        track("premium_pack_locked_ui", { pack: pack.key });
        toast(`${pack.label} is a Premium material — unlock it with MoodCard Premium.`, {
            action: {
                label: "See plans",
                onClick: () => navigate("/?premium=1"),
            },
            duration: 5000,
        });
    };

    // First-time micro-tour — fires the "flick to see the others" hint
    // ONLY when active sits at slot 0 for >= 3 seconds (the user is
    // hesitating instead of exploring). Resets if they touch ANY nav
    // mode. One-shot per browser via tourHasSeen("results-flick").
    const [hintReady, setHintReady] = useState(false);

    // ── Swipe deck state ──
    // Active card index in the 3-variant deck. Drag/wheel/keys mutate this.
    // The 3 styles stack vertically; the active one centers, neighbors peek
    // above/below at reduced scale + opacity. Tap-to-open replaces the old
    // "stacked list" scroll: one finger gesture takes you through all 3 in
    // ~1 second instead of a scroll-and-pick interaction.
    const [active, setActive] = useState(0);
    const cardCount = mood?.cards?.length || 0;
    const dragY = useMotionValue(0);
    const lockedRef = useRef(false);  // re-entrancy guard for fast multi-swipes

    const advance = (dir) => {
        if (lockedRef.current) return;
        if (cardCount === 0) return;
        // Soft wrap: scrolling past the end snaps back to bound rather than wrapping.
        const next = Math.max(0, Math.min(cardCount - 1, active + dir));
        if (next === active) return;
        lockedRef.current = true;
        setActive(next);
        tick();  // 8ms haptic — native-feel deck advance on supported devices
        track("results_deck_swipe", { from: active, to: next, dir });
        setTimeout(() => { lockedRef.current = false; }, 280);
    };

    // Keyboard arrows for desktop accessibility — Up/Down moves through deck,
    // Enter/Space opens the centered card. PageUp/PageDown jump to ends.
    useEffect(() => {
        const onKey = (e) => {
            if (!mood) return;
            if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); advance(1); }
            else if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); advance(-1); }
            else if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                const c = mood.cards[active];
                if (c) navigate(`/card/${id}/${c.style}`, { state: { mood } });
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mood, active, cardCount]);

    // Hint timer — now safely placed after `active` and `cardCount` exist.
    useEffect(() => {
        if (active !== 0 || cardCount < 2) {
            setHintReady(false);
            return;
        }
        const t = setTimeout(() => setHintReady(true), 3000);
        return () => clearTimeout(t);
    }, [active, cardCount]);


    if (loading || !mood) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-app">
                <span className="font-sans text-xs uppercase tracking-[0.3em] text-app-3">loading…</span>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="results-screen">
            <Toaster theme="dark" position="bottom-center" />
            <div className="absolute inset-0 aurora-wash opacity-40 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />

            <header className="relative z-10 max-w-md lg:max-w-5xl mx-auto px-6 pt-6 flex items-center justify-between">
                <button
                    onClick={() => navigate("/")}
                    className="flex items-center gap-2 text-app-2 hover:text-app transition-colors"
                    data-testid="results-back-button"
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-sans text-xs uppercase tracking-[0.3em]">new</span>
                </button>
                <div className="flex items-center gap-2">
                    <ThemeToggle />
                    <button
                        onClick={handleRegenerate}
                        disabled={regenerating}
                        data-testid="results-regenerate-button"
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-app/30 text-app-2 hover:text-app hover:bg-surface-2 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        <span className="font-sans text-[10px] uppercase tracking-[0.3em]">regen</span>
                    </button>
                </div>
            </header>

            <div className="relative z-10 max-w-md lg:max-w-5xl mx-auto px-6 pt-8 pb-24">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-3">
                        three ways to say it
                    </p>
                    <h2 className="font-serif font-light text-app text-3xl sm:text-4xl leading-tight tracking-tight">
                        pick the one<br /><span className="italic text-app-2">that fits.</span>
                    </h2>
                    <p className="mt-3 font-serif italic text-app-3 text-base max-w-[32ch]">
                        "{mood.feeling.length > 110 ? mood.feeling.slice(0, 110) + "…" : mood.feeling}"
                    </p>
                </motion.div>

                <div className="mt-7">
                    <PackSwitcher
                        value={mood.pack || "cinematic"}
                        onChange={handlePack}
                        loading={packLoading}
                        isPremium={hasPremiumPacks}
                        onPremiumLocked={handlePremiumLocked}
                    />
                </div>

                {/* Swipe deck — TikTok-style single-card viewer.
                    One card occupies the viewport at a time; the other two
                    live off-screen (translateY ±100%, opacity 0,
                    pointer-events:none) until flicked in. Five navigation
                    modes — drag, wheel, ArrowUp/Down + j/k, dot rail,
                    prev/next chevrons — so the "extra step" the user
                    flagged becomes one fast cinematic gesture instead of
                    a scroll-and-pick pause.

                    Why no peeks?
                    - "Pick the one that fits" needs FULL legibility of
                      whichever card is being considered. Peeks at scale
                      0.6 are unreadable miniatures.
                    - TikTok metaphor is one full-bleed surface at a time;
                      the user explicitly invoked that pattern.
                    - Dot rail + chevrons + "flick · tap to open" hint
                      give complete affordance without competing visuals. */}
                {/* Swipe deck — TikTok-style single-card viewer on MOBILE.
                    On DESKTOP (lg+), we render all three cards side-by-side
                    in a row so the picker is a single glance — no flick
                    needed. The deck/rail still mount for mobile users (lg:hidden). */}
                <div
                    className="relative mt-8 select-none overflow-x-hidden touch-pan-x lg:hidden"
                    style={{
                        height: "min(76vh, 640px)",
                    }}
                    data-testid="results-deck"
                    onWheel={(e) => {
                        if (Math.abs(e.deltaY) < 8) return;
                        advance(e.deltaY > 0 ? 1 : -1);
                    }}
                >
                    {mood.cards.map((card, i) => {
                        const offset = i - active;  // -1 above, 0 centered, +1 below
                        const isCenter = offset === 0;
                        // TikTok metaphor: one card occupies the viewport at
                        // a time, neighbors live OFF-SCREEN until flicked in.
                        // Translate fully out (±100% of container height) so
                        // the spring transition reads as "the next card flies
                        // in from below" rather than scaling in from the
                        // middle of nothing.
                        const yShift = offset * 100;  // % of container
                        return (
                            <motion.div
                                key={`${card.style}-${mood.pack}-${i}`}
                                drag={isCenter ? "y" : false}
                                dragConstraints={{ top: 0, bottom: 0 }}
                                dragElastic={0.18}
                                style={{
                                    y: isCenter ? dragY : 0,
                                    // pointerEvents off on hidden slots so a
                                    // ghosted peek can never intercept a tap
                                    // intended for the centered card.
                                    pointerEvents: isCenter ? "auto" : "none",
                                }}
                                onDragEnd={(_, info) => {
                                    const threshold = 60;
                                    const v = info.velocity.y;
                                    const o = info.offset.y;
                                    if (o < -threshold || v < -300) advance(1);
                                    else if (o > threshold || v > 300) advance(-1);
                                    dragY.set(0);
                                }}
                                animate={{
                                    // % offset so it adapts to any deck height
                                    y: `${yShift}%`,
                                    opacity: isCenter ? 1 : 0,
                                    zIndex: isCenter ? 10 : 0,
                                }}
                                transition={{
                                    type: "spring", stiffness: 240, damping: 30, mass: 0.55,
                                    opacity: { duration: 0.18 },
                                }}
                                className="absolute inset-0 flex items-start justify-center"
                                data-testid={`results-deck-slot-${i}`}
                                data-state={isCenter ? "active" : "peek"}
                            >
                                <button
                                    onClick={() => navigate(`/card/${id}/${card.style}`, { state: { mood } })}
                                    data-testid={`results-card-${card.style}`}
                                    className="btn-press w-full max-w-[360px] text-left group"
                                >
                                    <div className="relative">
                                        <MoodCard card={card} moodId={id} />
                                        <div className="mt-3 flex items-center justify-between px-1">
                                            <span className="font-sans text-xs uppercase tracking-[0.35em] text-app">
                                                {card.style}
                                            </span>
                                            <span className="flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.25em] text-app-2 group-hover:text-app transition-colors">
                                                open
                                                <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Deck rail — only on mobile (deck visible). On desktop we
                    render all 3 cards side-by-side below so the rail becomes
                    redundant. */}
                <div className="mt-3 flex items-center justify-between lg:hidden" data-testid="results-deck-rail">
                    <button
                        onClick={() => advance(-1)}
                        disabled={active === 0}
                        data-testid="results-deck-prev"
                        aria-label="Previous variant"
                        className="btn-press flex items-center justify-center h-9 w-9 text-app-3 hover:text-app disabled:opacity-25 transition-colors"
                    >
                        <ChevronUp className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <div className="flex items-center gap-2">
                        {mood.cards.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setActive(i)}
                                data-testid={`results-deck-dot-${i}`}
                                aria-label={`Go to card ${i + 1}`}
                                className="h-1.5 rounded-full transition-all"
                                style={{
                                    width: i === active ? "18px" : "6px",
                                    background: i === active ? "var(--app-text)" : "var(--app-text-3)",
                                    opacity: i === active ? 0.9 : 0.4,
                                }}
                            />
                        ))}
                    </div>
                    <button
                        onClick={() => advance(1)}
                        disabled={active === cardCount - 1}
                        data-testid="results-deck-next"
                        aria-label="Next variant"
                        className="btn-press flex items-center justify-center h-9 w-9 text-app-3 hover:text-app disabled:opacity-25 transition-colors"
                    >
                        <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>

                <p className="mt-4 text-center font-sans text-[10px] uppercase tracking-[0.32em] text-app-3 lg:hidden">
                    flick · tap to open
                </p>

                {/* Desktop-only: side-by-side 3-card row. Same testids as the
                    deck so flow-tests work cross-breakpoint. Each card is
                    independently tappable → /card/:id/:style. */}
                <div className="hidden lg:grid lg:grid-cols-3 lg:gap-6 lg:mt-10" data-testid="results-desktop-row">
                    {mood.cards.map((card, i) => (
                        <motion.button
                            key={`${card.style}-desk-${i}`}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ y: -4 }}
                            onClick={() => navigate(`/card/${id}/${card.style}`, { state: { mood } })}
                            data-testid={`results-desktop-card-${card.style}`}
                            className="btn-press text-left group"
                        >
                            <MoodCard card={card} moodId={id} />
                            <div className="mt-3 flex items-center justify-between px-1">
                                <span className="font-sans text-xs uppercase tracking-[0.35em] text-app">
                                    {card.style}
                                </span>
                                <span className="flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.25em] text-app-2 group-hover:text-app transition-colors">
                                    open
                                    <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                </span>
                            </div>
                        </motion.button>
                    ))}
                </div>

                {/* First-time hint — appears once per browser after the user
                    sits at slot 0 for 3s. Auto-dismisses on any pointerdown
                    OR after 6s. Stored via tourMarkSeen("results-flick"). */}
                <TourHint id="results-flick" show={hintReady} anchor="bottom-rail">
                    flick to see the others
                </TourHint>

                <button
                    onClick={handleRegenerate}
                    disabled={regenerating}
                    data-testid="results-regenerate-button-bottom"
                    className="mt-12 w-full py-4 rounded-full border border-app/30 bg-surface-1 text-app-2 font-sans text-sm tracking-wide hover:bg-surface-2 hover:text-app transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    not it — regenerate
                </button>
            </div>
        </div>
    );
}
